import type { ApplicationDto } from '@/api/backend/users/applications';
import type { PropertyPurpose } from '@/api/enums/property-purpose.enum';
import { SAVINGS_CONTRIBUTION_TO_PURCHASE } from '@/helpers/const';
import { mapFromApi } from '@/mappers';
import { periodic } from '@/mappers/helpers';
import type {
  ApplicantAsset,
  AssetAccount,
  AssetTypeEnum,
  DescriptionAsset,
  MotorVehicleAsset,
  PropertyAsset,
} from '@/types';
import type { ApiSchema, Institute, VehicleMake, VehicleModel } from '@/types/api';

type FundsAssetDto = ApiSchema['FundsAssetDto'] & {
  assetAmount?: number;
  endDebtContribution?: number;
  towardsPurchase?: number;
  institution?: Institute | string;
  vehicleMake?: VehicleMake;
  vehicleModel?: VehicleModel;
  yearBought?: number;
  assetDescription?: string;
};

const mapAsset = (data: FundsAssetDto): AssetAccount | MotorVehicleAsset | DescriptionAsset => {
  const hasResidualDebtContribution = (data.endDebtContribution ?? 0) > 0;

  if (data.assetType === 'motor_vehicles') {
    return {
      id: data.id ?? '',
      type: 'motor_vehicles',
      make: data.vehicleMake?.name ?? '',
      model: data.vehicleModel?.name ?? '',
      yearBought: data.yearBought?.toString() ?? '',
      value: data.assetAmount ?? 0,
      assetNumber: 0,
      useAssetForResidualDebt: hasResidualDebtContribution,
      amountTowardsResidualDebt: hasResidualDebtContribution ? data.endDebtContribution : undefined,
    };
  }

  if (data.assetType === 'machinery' || data.assetType === 'equipment') {
    return {
      id: data.id ?? '',
      type: data.assetType,
      description: data.assetDescription ?? '',
      value: data.assetAmount ?? 0,
      assetNumber: 0,
      useAssetForResidualDebt: hasResidualDebtContribution,
      amountTowardsResidualDebt: hasResidualDebtContribution ? data.endDebtContribution : undefined,
    };
  }

  return {
    id: data.id ?? '',
    type: data.assetType as Exclude<AssetTypeEnum, 'property' | 'motor_vehicles' | 'machinery' | 'equipment'>,
    institution: (<Institute>data.institution)?.name || <string>data.institution || '',
    value: data.assetAmount ?? 0,
    assetNumber: 0,
    useAssetForResidualDebt: hasResidualDebtContribution,
    amountTowardsResidualDebt: hasResidualDebtContribution ? data.endDebtContribution : undefined,
  };
};

type AssetsPropertyDto = ApiSchema['AssetsPropertyDto'] & {
  amountOwed?: number;
  liabilityLimit?: number;
  payoutAmount?: number;
  isNewBuild?: boolean;
  isContractExchanged?: boolean;
};

const mapPropertyAsset = (data: AssetsPropertyDto): PropertyAsset => {
  return {
    id: data.id ?? '',
    type: 'property',
    address: mapFromApi.address(data),
    propertyValue: data.propertyValue ?? 0,
    propertyPurpose: (data.propertyPurpose as PropertyPurpose) ?? 'owner_occupied',
    rentalIncome: periodic(data.rentalIncome, 'monthly'),
    amountOwing: data.amountOwedApplicationValue ?? 0,
    assetNumber: 0,
    isNewBuild: data.isNewBuild ?? undefined,
    isContractExchanged: data.isContractExchanged ?? undefined,
  };
};

export const assets = (applicantId: string, application: ApplicationDto): ApplicantAsset[] => {
  const fundAssets = (application.fundsAssets ?? [])
    .filter((a: FundsAssetDto) => a.applicantId == applicantId)
    .map(mapAsset)
    // Exclude "Savings to contribute to purchase" from assets (this is the savingsAmount for loanDetails)
    .filter((a) => !('institution' in a) || a.institution !== SAVINGS_CONTRIBUTION_TO_PURCHASE);

  const properties: PropertyAsset[] = (application.assetsProperties ?? [])
    .filter((a: AssetsPropertyDto) => a.applicantId === applicantId)
    .map(mapPropertyAsset);

  return [...fundAssets, ...properties];
};
